import { useState, useId } from 'react';
import { KeyRound, Eye, EyeOff, AlertTriangle, LogIn, Lock, User } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/Card.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Input } from '../../components/ui/Input.jsx';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/Alert.jsx';

/**
 * Translates raw API or client errors into clear German messages for the user.
 */
export function formatAuthErrorMessage(rawError, isDemo = false) {
  if (!rawError) return '';
  const msg = typeof rawError === 'string' ? rawError : rawError.message || String(rawError);

  if (msg === 'invalid_credentials' || msg.includes('invalid_credentials') || msg.includes('401')) {
    return isDemo
      ? 'Ungültige Anmeldedaten. Bitte überprüfen Sie Benutzername und Passwort.'
      : 'Ungültiger API-Schlüssel. Zugriff verweigert.';
  }
  if (msg === 'auth_not_configured' || msg.includes('auth_not_configured') || msg.includes('503')) {
    return 'Der Authentifizierungsdienst ist auf dem Server nicht konfiguriert (Server-Secrets fehlen).';
  }
  if (msg.includes('inactive') || msg.includes('disabled') || msg.includes('gesperrt')) {
    return 'Dieses Benutzerkonto ist deaktiviert oder gesperrt. Bitte kontaktieren Sie die Administration.';
  }
  if (msg.includes('403') || msg.includes('Forbidden') || msg.includes('forbidden')) {
    return 'Zugriff verweigert: Unzureichende Berechtigungen für diesen Bereich.';
  }
  if (msg.includes('Network') || msg.includes('Failed to fetch') || msg.includes('Verbindung')) {
    return 'Netzwerkfehler: Verbindung zum Authentifizierungsserver fehlgeschlagen.';
  }
  return msg;
}

export function AdminAuthGuard({
  apiKey,
  isAuthenticated,
  validating,
  error,
  onAuthenticate,
  onTenantAdminLogin,
  role = null,
  children,
}) {
  const [inputKey, setInputKey] = useState(apiKey || '');
  const [showPassword, setShowPassword] = useState(false);
  const [showDemoPassword, setShowDemoPassword] = useState(false);
  const [validationError, setValidationError] = useState('');
  // Demo-/Interessenten-Login (Rolle tenant_admin)
  const [demoMode, setDemoMode] = useState(false);
  const [demoLogin, setDemoLogin] = useState('');
  const [demoPassword, setDemoPassword] = useState('');
  const [demoError, setDemoError] = useState('');

  const errorDescId = useId();

  if (isAuthenticated) {
    return <>{children}</>;
  }

  const rawActiveError = demoMode ? (demoError || error) : (error || validationError);
  const displayError = formatAuthErrorMessage(rawActiveError, demoMode);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (demoMode) {
      const trimmedLogin = demoLogin.trim();
      if (!trimmedLogin && !demoPassword) {
        setDemoError('Bitte geben Sie Benutzername und Passwort ein.');
        return;
      }
      if (!trimmedLogin) {
        setDemoError('Bitte geben Sie Ihren Benutzernamen ein.');
        return;
      }
      if (!demoPassword) {
        setDemoError('Bitte geben Sie Ihr Passwort ein.');
        return;
      }
      setDemoError('');
      onTenantAdminLogin?.(trimmedLogin, demoPassword);
      return;
    }
    if (!inputKey || !inputKey.trim()) {
      setValidationError('Bitte geben Sie einen gültigen API-Schlüssel ein.');
      return;
    }
    setValidationError('');
    onAuthenticate(inputKey.trim());
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center p-4 sm:p-6 lg:p-8 bg-[var(--background)]">
      <Card className="w-full max-w-md border-[var(--border)] shadow-xl bg-[var(--surface,white)] rounded-[var(--radius-xl,0.75rem)]">
        <CardHeader className="space-y-3 text-center pb-6 border-b border-[var(--border)]">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--primary)]/10 text-[var(--primary)] border border-[var(--primary)]/20 shadow-2xs">
            <Lock className="h-7 w-7" aria-hidden="true" />
          </div>
          <div className="space-y-1">
            <CardTitle className="font-display text-2xl font-bold tracking-tight text-[var(--foreground)]">
              {demoMode ? 'Shop-Verwaltung' : 'ASL Control-Plane'}
            </CardTitle>
            <CardDescription className="text-sm text-[var(--secondary)] leading-relaxed">
              {demoMode
                ? 'Verwalten Sie Inhalte, Theme und Preise Ihres Shops.'
                : 'Bitte authentifizieren Sie sich mit Ihrem Administrator-Schlüssel, um auf die Mandantenverwaltung zuzugreifen.'}
            </CardDescription>
          </div>
        </CardHeader>

        <form
          onSubmit={handleSubmit}
          noValidate
          aria-label={demoMode ? 'Anmeldung zur Shop-Verwaltung' : 'Anmeldung zur ASL Control-Plane'}
        >
          <CardContent className="space-y-4 pt-6">
            {displayError && (
              <Alert
                variant="destructive"
                id="auth-error-alert"
                role="alert"
                aria-live="assertive"
                className="border-red-500/30 bg-red-500/10 text-red-950"
              >
                <AlertTriangle className="h-4 w-4 text-red-600" aria-hidden="true" />
                <AlertTitle className="font-semibold text-red-900">
                  {demoMode ? 'Anmeldung fehlgeschlagen' : 'Zugriff verweigert'}
                </AlertTitle>
                <AlertDescription id={errorDescId} className="text-red-800 text-xs mt-1">
                  {displayError}
                </AlertDescription>
              </Alert>
            )}

            {demoMode ? (
              <>
                <div className="space-y-1.5">
                  <label
                    htmlFor="tenant-admin-login"
                    className="block text-xs font-semibold uppercase tracking-wider text-[var(--foreground)]"
                  >
                    Benutzername / Login
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-[var(--secondary)]/70">
                      <User className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <Input
                      id="tenant-admin-login"
                      type="text"
                      autoComplete="username"
                      value={demoLogin}
                      onChange={(e) => {
                        setDemoLogin(e.target.value);
                        if (demoError) setDemoError('');
                      }}
                      placeholder="Ihr Partner-Benutzername"
                      className="pl-9 text-sm border-[var(--border)] focus-visible:ring-[var(--ring,var(--primary))]"
                      autoFocus
                      aria-required="true"
                      aria-invalid={Boolean(displayError)}
                      aria-describedby={displayError ? errorDescId : undefined}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="tenant-admin-password"
                    className="block text-xs font-semibold uppercase tracking-wider text-[var(--foreground)]"
                  >
                    Passwort
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-[var(--secondary)]/70">
                      <KeyRound className="h-4 w-4" aria-hidden="true" />
                    </div>
                    <Input
                      id="tenant-admin-password"
                      type={showDemoPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={demoPassword}
                      onChange={(e) => {
                        setDemoPassword(e.target.value);
                        if (demoError) setDemoError('');
                      }}
                      placeholder="Ihr Passwort"
                      className="pl-9 pr-10 text-sm border-[var(--border)] focus-visible:ring-[var(--ring,var(--primary))]"
                      aria-required="true"
                      aria-invalid={Boolean(displayError)}
                      aria-describedby={displayError ? errorDescId : undefined}
                    />
                    <button
                      type="button"
                      onClick={() => setShowDemoPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-[var(--secondary)] hover:text-[var(--foreground)] focus:outline-none cursor-pointer"
                      aria-label={showDemoPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                      aria-pressed={showDemoPassword}
                    >
                      {showDemoPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-1.5">
                <label
                  htmlFor="admin-api-key"
                  className="block text-xs font-semibold uppercase tracking-wider text-[var(--foreground)]"
                >
                  Admin API-Schlüssel
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-[var(--secondary)]/70">
                    <KeyRound className="h-4 w-4" aria-hidden="true" />
                  </div>
                  <Input
                    id="admin-api-key"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={inputKey}
                    onChange={(e) => {
                      setInputKey(e.target.value);
                      if (validationError) setValidationError('');
                    }}
                    placeholder="Admin-Schlüssel eingeben..."
                    className="pl-9 pr-10 font-mono text-sm border-[var(--border)] focus-visible:ring-[var(--ring,var(--primary))]"
                    autoFocus
                    aria-required="true"
                    aria-invalid={Boolean(displayError)}
                    aria-describedby={displayError ? errorDescId : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-[var(--secondary)] hover:text-[var(--foreground)] focus:outline-none cursor-pointer"
                    aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
                  </button>
                </div>
              </div>
            )}
          </CardContent>

          <CardFooter className="flex flex-col gap-3 pt-2 pb-6">
            <Button
              type="submit"
              className="w-full bg-[var(--primary)] text-white font-semibold shadow-xs hover:brightness-110 active:scale-[0.99] cursor-pointer"
              disabled={validating}
              aria-busy={validating}
            >
              {validating ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Prüfe Autorisierung...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="h-4 w-4" aria-hidden="true" />
                  {demoMode ? 'In Shop-Verwaltung anmelden' : 'Anmelden'}
                </span>
              )}
            </Button>
            {onTenantAdminLogin && (
              <button
                type="button"
                data-testid="toggle-demo-login"
                onClick={() => {
                  setDemoMode((prev) => !prev);
                  setDemoError('');
                  setValidationError('');
                }}
                className="text-xs text-[var(--secondary)] hover:text-[var(--foreground)] underline underline-offset-4 cursor-pointer transition-colors"
              >
                {demoMode ? 'Stattdessen mit Admin-Schlüssel anmelden' : 'Demo-Zugang? Hier anmelden'}
              </button>
            )}
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}